import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import { isToday } from 'date-fns';
import delay from 'lodash/delay';
import uniq from 'lodash/uniq';
import { ReactElement, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import Button from '~/ui/components/common/Button';
import ConfirmModal from '~/ui/components/common/ConfirmModal';
import NavigationConfirmModal from '~/ui/components/common/NavigationConfirmModal';
import MembersFields from '../MembersFields';

import { useStoreActions, useStoreState } from '~/store/hooks';
import useRole from '~/store/user/hooks/useRole';
import { ITeamMemberMapped, prepareDefaultValues } from '~/utils/clientAllocation';
import { getLocalDateNoFormat } from '~/utils/date/date';
import { extractErrorMessage } from '~/utils/error/error';
import extractFullName from '~/utils/text/extractFullName';
import useFormStorage from '../hooks/useFormStorage';
import extractArchivedClients from '../helpers/extractArchivedClients';
import useClientAllocationAvailability from '../hooks/useClientAllocationAvailability';
import useEditClientAllocation from '../hooks/useEditClientAllocation';

import { IClientOptionByTeam } from '~/store/client/types';
import {
  IAllocationClient,
  IAllocationClientsByGroup,
  IClientAllocationDetailed,
} from '~/services/api/clientAllocation/types';
import { AllocationMode } from '../constants/allocationMode';
import { EDIT_DRAFT_ALLOCATION_KEY } from '~/services/localStorage/allocations';
import { IOption } from '~/types';
import { NETWORK, VIEW_CLIENT_ALLOCATION, VIEW_MY_CLIENT_ALLOCATION } from '~/ui/constants/paths';

import styles from './EditClientAllocation.module.scss';

interface IProps {
  teamMembers: ITeamMemberMapped[];
  clients: IOption[];
  clientAllocation: IClientAllocationDetailed[];
  actTeamId: string;
  assignedClients?: number[];
  clinicId: string;
  locationGroups: IAllocationClientsByGroup[];
  isLocationGroupAllocation: boolean;
}

const EditClientAllocationForm = ({
  clientAllocation,
  teamMembers,
  clients,
  actTeamId,
  clinicId,
  locationGroups,
  isLocationGroupAllocation,
}: IProps): ReactElement => {
  const [isAdding, setIsAdding] = useState(false);
  // * details used to transfer data from form to modal
  const [details, setDetails] = useState(null);
  const [withClearDraft, setWithClearDraft] = useState(false);

  const navigate = useNavigate();

  const { dateAvailability } = useStoreState(state => state.clientAllocation);

  const defValues = {
    date: clientAllocation[0]?.date,
    allocationMode: isLocationGroupAllocation
      ? AllocationMode.BY_LOCATION_GROUP
      : AllocationMode.BY_CLIENT,
    ...prepareDefaultValues(clientAllocation),
  };

  const form = useForm({
    defaultValues: defValues,
  });
  const {
    formState: { errors, isDirty },
    watch,
    handleSubmit,
  } = form;

  const { isRestored, clearDraft } = useFormStorage(form, {
    key: EDIT_DRAFT_ALLOCATION_KEY,
    conditionKey: 'date',
  });

  const { date: formDate, allocationMode, ...formValues } = watch();

  const userIdsWithClients = useMemo(
    () => clientAllocation?.filter(item => !item.isArchived)?.map(item => item.user.id),
    [clientAllocation],
  );

  const isDisabledCreateButton =
    !!dateAvailability?.clients.length && dateAvailability?.clients.some(c => !c.canBeCreated);

  const { showError, showNotify } = useStoreActions(actions => actions.snackbar);

  const { isActTeam, isActTeamMember, isGlobalUser, isActTeamLeader } = useRole();

  const newAssignedClients = formValues as {
    [key: string]: number[];
  };

  const { list, current } = useStoreState(store => store.client.clientOptionsByTeam);

  const selectedClientOptions: IClientOptionByTeam[] = useMemo(() => {
    const clientIds = Object.keys(newAssignedClients).reduce((acc, key) => {
      if (newAssignedClients && key.startsWith('clients_') && newAssignedClients[key]) {
        const newClients = newAssignedClients[key] || [];
        acc.push(...newClients);
      }
      return acc;
    }, [] as number[]);
    return list.filter(item => clientIds.includes(item.id));
  }, [list, newAssignedClients]);

  const deletedMembers = useMemo(
    () =>
      teamMembers.filter(
        teamMember => teamMember.isDeleted && userIdsWithClients.includes(teamMember.id),
      ),
    [teamMembers, userIdsWithClients],
  );

  const activeMembers = useMemo(
    () => teamMembers.filter(teamMember => !teamMember.isDeleted),
    [teamMembers],
  );

  const onSubmitHandler = useEditClientAllocation({
    clinicId,
    isGlobalUser,
    isActTeamMember,
    clientAllocation,
    actTeamId,
    current: selectedClientOptions,
    locationGroups,
  });

  const onSubmit = async (values: Record<string, any>) => {
    try {
      const archivedClients = extractArchivedClients(clientAllocation);

      if (archivedClients.length) {
        const archivedClientsNames = uniq(
          archivedClients.map((client: IAllocationClient) => extractFullName(client)),
        ).join(', ');
        const isMultipleArchived = archivedClients.length > 1;
        showError(
          `The archived client${isMultipleArchived ? 's' : ''} ${archivedClientsNames} ha${
            isMultipleArchived ? 've' : 's'
          } been removed from the created allocation`,
        );
      }

      delay(
        async () => {
          setIsAdding(true);

          await onSubmitHandler(values);

          showNotify({ message: 'Client allocation successfully updated' });
          clearDraft();
        },
        archivedClients.length ? 3000 : 0,
      );
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      setIsAdding(false);
      setDetails(null);
    }
  };

  const openConfirmationModal = ({ date, ...rest }: any) => {
    if (getLocalDateNoFormat(date) < new Date() && !isToday(getLocalDateNoFormat(date))) {
      setDetails(rest);
      return;
    }
    onSubmit(rest);
  };

  const handleCancel = () => {
    setWithClearDraft(true);
    if (isGlobalUser || isActTeam) {
      const route = isGlobalUser ? VIEW_MY_CLIENT_ALLOCATION : VIEW_CLIENT_ALLOCATION;
      const finalRoute = route
        .replace(':actTeamId', actTeamId)
        .replace(':clientAllocationDate', clientAllocation[0].date.split('T')[0]);
      navigate(finalRoute);
    } else navigate(NETWORK);
  };

  // List of all clients
  const clientsToRender = useMemo(() => {
    if (isGlobalUser) {
      return current?.map(client => ({
        name: client.name,
        id: Number(client.id),
        photo: client.photo,
        teams: client.teams,
      }));
    }
    return clients.map(client => ({
      name: client.label,
      id: Number(client.value),
      photo: client.photo,
      teams: client.teams,
    }));
  }, [isGlobalUser, clients, current]);

  const clientAllocationClientsIds = useMemo(
    () => clientAllocation.flatMap(item => item.user.clients.map(c => c.id)),
    [clientAllocation],
  );
  const assignedClients = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(formValues as { [key: string]: number[] | number }).filter(
          ([key]) =>
            key.startsWith('clients') &&
            Array.isArray((formValues as { [key: string]: number[] | number })[key]),
        ),
      ) as { [key: string]: number[] },
    [formValues],
  );

  useClientAllocationAvailability({
    dateRange: {
      from: clientAllocation[0]?.date,
      to: clientAllocation[0]?.date,
    },
    clientsList: assignedClients,
    assignedTeams: {},
    clientAllocationClientsIds,
    clinicId,
    teamId: Number(actTeamId),
  });

  const leavePageDescription =
    'Are you sure you want to cancel Edit Client Allocation"? All the changes will be discarded.';

  const editPastAllocationDescription =
    'Caution. You are modifying a past date. Are you sure you want to proceed?';

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(openConfirmationModal)}>
        <Paper>
          <Box p={2}>
            <Grid container spacing={4}>
              <Grid item sm={12}>
                <Typography variant="subtitle1" className={styles.assignedClientsTitle}>
                  {allocationMode === AllocationMode.BY_CLIENT
                    ? 'Assigned Clients per Team Member'
                    : 'Location Groups per Team Member'}
                </Typography>
              </Grid>
            </Grid>

            {isRestored && (
              <Grid container spacing={3} className={styles.formWrapper}>
                <MembersFields
                  members={activeMembers}
                  clients={clients}
                  clientAllocationDate={clientAllocation[0]?.date}
                  clientAllocations={clientAllocation}
                  isGlobalUser={isGlobalUser}
                  assignedClients={newAssignedClients}
                  clinicId={clinicId}
                  clientsList={clientsToRender as IClientOptionByTeam[]}
                  locationGroups={locationGroups}
                  isActTeamLeader={isActTeamLeader}
                  validateAvailability
                />

                {!!deletedMembers.length && (
                  <>
                    <Grid item sm={12}>
                      <h5 className={styles.subTitle}>Deleted/Deactivated Team Members</h5>
                    </Grid>
                    <MembersFields
                      members={deletedMembers}
                      clients={clients}
                      clientAllocationDate={clientAllocation[0]?.date}
                      clientAllocations={clientAllocation}
                      isGlobalUser={isGlobalUser}
                      assignedClients={newAssignedClients}
                      clinicId={clinicId}
                      clientsList={clientsToRender as IClientOptionByTeam[]}
                      locationGroups={locationGroups}
                      isActTeamLeader={isActTeamLeader}
                    />
                  </>
                )}
              </Grid>
            )}
          </Box>
        </Paper>

        <div className={styles.buttons}>
          <div className={styles.buttonWrapper}>
            <Button color="primary" variant="outlined" onClick={handleCancel}>
              Cancel
            </Button>
          </div>
          <Button
            color="primary"
            variant="contained"
            type="submit"
            disabled={Object.keys(errors).length > 0 || isDisabledCreateButton}
          >
            Save Changes
          </Button>
        </div>

        <NavigationConfirmModal
          when={isDirty && !isAdding}
          description={leavePageDescription}
          beforeConfirm={withClearDraft ? clearDraft : undefined}
        />
        {/* this modal triggers only when allocation date is in the past */}
        {details && (
          <ConfirmModal
            onConfirm={() => {
              onSubmit(details);
            }}
            onClose={() => setDetails(null)}
            description={editPastAllocationDescription}
            confirmText="Proceed"
          />
        )}
      </form>
    </FormProvider>
  );
};

export default EditClientAllocationForm;
